import { useEffect, useMemo, useState } from 'react';
import { Loader2, Users, UserCheck, UserX, Store, BellOff } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  iconBgBlue,
  iconBgGreen,
  iconBgAmber,
  iconBgPurple,
  iconBgRed,
} from '../../../styling/stylingHelper';
import { StatCard } from '../../ui/StatCard';
import { AdminLayout } from './AdminLayout';
import {
  getUserActivity,
  type UserActivityResponse,
} from '../../../services/admin/userActivityService';

const DAY_MS = 24 * 60 * 60 * 1000;

// Relative "time ago" + recency colour. Never-seen and >14d are red, 7–14d amber.
const relativeTime = (iso: string | null): { label: string; className: string } => {
  if (!iso) return { label: 'never', className: 'text-red-600 dark:text-red-400' };
  const then = new Date(iso).getTime();
  const days = (Date.now() - then) / DAY_MS;
  let label: string;
  if (days < 1) label = 'today';
  else if (days < 2) label = 'yesterday';
  else if (days < 14) label = `${Math.floor(days)}d ago`;
  else if (days < 60) label = `${Math.floor(days / 7)}w ago`;
  else label = `${Math.floor(days / 30)}mo ago`;
  const className =
    days < 7
      ? 'text-green-600 dark:text-green-400'
      : days < 14
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-red-600 dark:text-red-400';
  return { label, className };
};

const pctOf = (num: number, den: number): string => (den > 0 ? `${Math.round((num / den) * 100)}%` : '—');

const AdminUserActivityPage = () => {
  const [data, setData] = useState<UserActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientFilter, setClientFilter] = useState<string>('all');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getUserActivity());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const clients = useMemo(
    () => (data ? data.clients.filter((c) => clientFilter === 'all' || c.clientId === clientFilter) : []),
    [data, clientFilter]
  );

  // Aggregate KPIs over the (filtered) clients. Users are de-duped by id, since
  // one user can belong to several restaurants. "Quiet" restaurants = nobody seen
  // in the last 14 days — the at-a-glance churn-risk signal.
  const kpis = useMemo(() => {
    const lastSeenByUser = new Map<string, number | null>();
    let restaurants = 0;
    let quietRestaurants = 0;
    for (const c of clients) {
      for (const r of c.restaurants) {
        restaurants += 1;
        let mostRecent = -Infinity;
        for (const u of r.users) {
          const ts = u.lastSeen ? new Date(u.lastSeen).getTime() : null;
          if (ts !== null && ts > mostRecent) mostRecent = ts;
          const prev = lastSeenByUser.get(u.userId);
          if (prev === undefined || (ts !== null && (prev === null || ts > prev))) {
            lastSeenByUser.set(u.userId, ts);
          }
        }
        const quiet = mostRecent === -Infinity || Date.now() - mostRecent > 14 * DAY_MS;
        if (r.users.length > 0 && quiet) quietRestaurants += 1;
      }
    }
    const totalUsers = lastSeenByUser.size;
    let active7 = 0;
    let active30 = 0;
    let dormant = 0;
    for (const ts of Array.from(lastSeenByUser.values())) {
      if (ts === null) {
        dormant += 1;
        continue;
      }
      const days = (Date.now() - ts) / DAY_MS;
      if (days <= 7) active7 += 1;
      if (days <= 30) active30 += 1;
      if (days > 30) dormant += 1;
    }
    return { totalUsers, active7, active30, dormant, restaurants, quietRestaurants };
  }, [clients]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className={cn(primaryText, 'text-2xl', semiBoldText)}>User Activity</h2>
            <p className={cn(secondaryText, 'mt-1', smallTextSize)}>
              Last time each restaurant's staff/admin opened the app, from PostHog page views
              (matched on Supabase user id). Helps spot clients who have gone quiet.
            </p>
          </div>

          {data && (
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className={cn(
                'px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 border-none',
                primaryText,
                smallTextSize
              )}
            >
              <option value="all">All clients</option>
              {data.clients.map((c) => (
                <option key={c.clientId} value={c.clientId}>
                  {c.clientName || c.clientEmail}
                </option>
              ))}
            </select>
          )}
        </div>

        {!loading && !error && data && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatCard
              icon={<Users className="w-6 h-6" style={{ color: '#2E90FA' }} />}
              iconBg={iconBgBlue}
              title="Users tracked"
              value={String(kpis.totalUsers)}
            />
            <StatCard
              icon={<UserCheck className="w-6 h-6" style={{ color: '#079455' }} />}
              iconBg={iconBgGreen}
              title="Active 7d"
              value={data.configured ? `${kpis.active7} (${pctOf(kpis.active7, kpis.totalUsers)})` : '—'}
            />
            <StatCard
              icon={<Users className="w-6 h-6" style={{ color: '#2E90FA' }} />}
              iconBg={iconBgBlue}
              title="Active 30d"
              value={data.configured ? `${kpis.active30} (${pctOf(kpis.active30, kpis.totalUsers)})` : '—'}
            />
            <StatCard
              icon={<UserX className="w-6 h-6" style={{ color: '#F59E0B' }} />}
              iconBg={iconBgAmber}
              title="Dormant >30d"
              value={data.configured ? String(kpis.dormant) : '—'}
            />
            <StatCard
              icon={<Store className="w-6 h-6" style={{ color: '#6938EF' }} />}
              iconBg={iconBgPurple}
              title="Restaurants"
              value={String(kpis.restaurants)}
            />
            <StatCard
              icon={<BellOff className="w-6 h-6" style={{ color: '#D92D20' }} />}
              iconBg={iconBgRed}
              title="Quiet 14d"
              value={data.configured ? String(kpis.quietRestaurants) : '—'}
            />
          </div>
        )}

        {data && !data.configured && (
          <div className={cn(cardClass, 'p-4 border-amber-300 dark:border-amber-700')}>
            <p className="text-amber-700 dark:text-amber-300 text-sm">
              PostHog read access is not configured on the server, so last-seen times are unavailable.
              Set <code>POSTHOG_PERSONAL_API_KEY</code> (a PostHog personal API key) in the server env to
              enable this. The user/restaurant structure below is still shown.
            </p>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className={smallTextSize}>Loading user activity…</span>
          </div>
        )}

        {error && !loading && (
          <div className={cn(cardClass, 'p-4 border-red-300 dark:border-red-700')}>
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && data && (
          <div className="space-y-6">
            {clients.map((client) => (
              <div key={client.clientId} className={cn(cardClass, 'overflow-hidden')}>
                <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <span className={cn(primaryText, semiBoldText, smallTextSize)}>
                    {client.clientName || client.clientEmail}
                  </span>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {client.restaurants.map((r) => (
                    <div key={r.restaurantId} className="px-4 py-3">
                      <p className={cn(secondaryText, 'text-xs mb-2 uppercase tracking-wide')}>
                        {r.restaurantName}
                      </p>
                      {r.users.length === 0 ? (
                        <p className={cn(secondaryText, smallTextSize)}>No users.</p>
                      ) : (
                        <div className="flex flex-wrap gap-x-6 gap-y-1">
                          {r.users.map((u) => {
                            const rel = relativeTime(u.lastSeen);
                            return (
                              <div
                                key={u.userId}
                                className="flex items-center gap-2"
                                title={u.lastSeen ? new Date(u.lastSeen).toLocaleString() : 'Never seen'}
                              >
                                <span className={cn(primaryText, smallTextSize)}>{u.email}</span>
                                <span className={cn('text-xs', secondaryText)}>({u.role})</span>
                                <span className={cn('text-xs', semiBoldText, rel.className)}>
                                  {data.configured ? rel.label : '—'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUserActivityPage;
